import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AdminCreateTenantBodySchema, type AdminBusinessHour } from '../../../src/shared/admin';
import {
  TenantPlanSchema,
  TenantStatusSchema,
  TenantVerticalSchema,
  type TenantPlan,
  type TenantStatus,
  type TenantVertical,
} from '../../../src/shared/tenants';
import { adminKeys, createAdminTenant } from '../../api/admin';
import { ErrorNotice } from '../../components/ErrorNotice';
import { slugifyWorkspaceName } from '../onboarding/slugify';

/**
 * `/admin/tenants/new` — super-user "new tenant setup" wizard
 * (`POST /api/admin/tenants`): owner user info, company info, account info
 * (plan/status/trial/Stripe), and optional seeded business hours. On success
 * the flow continues to the tenant's Secrets page so provider API keys can be
 * entered immediately.
 */

const VERTICAL_LABELS: Record<TenantVertical, string> = {
  hvac: 'HVAC',
  plumbing: 'Plumbing',
  electrical: 'Electrical',
  roofing: 'Roofing',
};

const US_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
] as const;

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/** "HH:MM" from a time input → minutes from local midnight. */
function timeToMinutes(value: string): number {
  const [hours = 0, minutes = 0] = value.split(':').map(Number);
  return hours * 60 + minutes;
}

const inputClass =
  'mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none';
const labelClass = 'block text-sm font-medium text-slate-700';

export function NewTenantPage(): React.JSX.Element {
  const queryClient = useQueryClient();

  // Owner info.
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerName, setOwnerName] = useState('');
  // Company info.
  const [companyName, setCompanyName] = useState('');
  const [vertical, setVertical] = useState<TenantVertical>('hvac');
  const [timezone, setTimezone] = useState<string>('America/New_York');
  // Account info.
  const [plan, setPlan] = useState<TenantPlan>('trial');
  const [status, setStatus] = useState<TenantStatus>('active');
  const [trialDays, setTrialDays] = useState(14);
  const [createStripeCustomer, setCreateStripeCustomer] = useState(false);
  // Business hours.
  const [seedHours, setSeedHours] = useState(true);
  const [hourDays, setHourDays] = useState<boolean[]>([false, true, true, true, true, true, false]);
  const [openTime, setOpenTime] = useState('08:00');
  const [closeTime, setCloseTime] = useState('17:00');

  const [fieldError, setFieldError] = useState<string | null>(null);

  const [createdTenant, setCreatedTenant] = useState<{ id: string; name: string } | null>(null);

  const createMutation = useMutation({
    mutationFn: createAdminTenant,
    onSuccess: ({ tenant }) => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.tenants });
      // Stay on this page to show the phone-number nudge rather than
      // immediately routing away — a tenant with no attached number silently
      // drops every inbound call and SMS until an operator visits /numbers.
      setCreatedTenant({ id: tenant.id, name: tenant.name });
    },
  });

  const slug = slugifyWorkspaceName(companyName);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const businessHours: AdminBusinessHour[] = seedHours
      ? hourDays.flatMap((enabled, weekday) =>
          enabled
            ? [
                {
                  weekday,
                  startMinute: timeToMinutes(openTime),
                  endMinute: timeToMinutes(closeTime),
                  slotMinutes: 60,
                },
              ]
            : []
        )
      : [];
    const parsed = AdminCreateTenantBodySchema.safeParse({
      owner: { email: ownerEmail, name: ownerName },
      company: { name: companyName, slug, vertical, timezone },
      account: {
        plan,
        status,
        trialEndsAt: plan === 'trial' ? Date.now() + trialDays * 24 * 60 * 60 * 1000 : null,
        createStripeCustomer,
      },
      businessHours,
    });
    if (!parsed.success) {
      setFieldError(parsed.error.issues[0]?.message ?? 'Check the form and try again.');
      return;
    }
    setFieldError(null);
    createMutation.mutate(parsed.data);
  };

  if (createdTenant !== null) {
    return (
      <main className="mx-auto w-full max-w-2xl px-6 py-10">
        <h1 className="text-2xl font-semibold tracking-tight">Tenant created</h1>
        <p className="mt-2 text-slate-600">
          <span className="font-medium text-slate-900">{createdTenant.name}</span> is provisioned.
        </p>

        <div
          className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800"
          data-testid="numbers-nudge"
        >
          <p className="font-medium">This tenant cannot receive calls or texts yet.</p>
          <p className="mt-1">
            Attach a phone number before going live —{' '}
            <Link
              to={`/admin/tenants/${createdTenant.id}/numbers`}
              className="font-medium underline underline-offset-4 hover:text-amber-900"
            >
              manage phone numbers
            </Link>
            .
          </p>
        </div>

        <div className="mt-6 flex items-center gap-4">
          <Link
            to={`/admin/tenants/${createdTenant.id}/numbers`}
            className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
          >
            Manage phone numbers
          </Link>
          <Link
            to={`/admin/tenants/${createdTenant.id}/secrets`}
            className="text-sm font-medium text-slate-600 underline underline-offset-4 hover:text-slate-900"
          >
            Enter provider secrets
          </Link>
          <Link
            to="/admin"
            className="text-sm font-medium text-slate-600 underline underline-offset-4 hover:text-slate-900"
          >
            Back to admin
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="mx-auto w-full max-w-2xl px-6 py-10">
      <div className="flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">New tenant setup</h1>
        <Link
          to="/admin"
          className="text-sm font-medium text-slate-600 underline underline-offset-4 hover:text-slate-900"
        >
          Back to admin
        </Link>
      </div>
      <p className="mt-2 text-slate-600">
        Provision a workspace with its owner, plan, and defaults. Provider API keys are entered on
        the next screen.
      </p>

      <form className="mt-6 space-y-6" onSubmit={handleSubmit} noValidate>
        <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-medium">Owner (user info)</h2>
          <p className="mt-1 text-sm text-slate-600">
            Becomes the workspace owner. An existing user with this email is linked; a new one is
            created and signs in via email code.
          </p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="owner-name" className={labelClass}>
                Full name
              </label>
              <input
                id="owner-name"
                type="text"
                autoComplete="off"
                required
                maxLength={120}
                value={ownerName}
                onChange={(event) => setOwnerName(event.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="owner-email" className={labelClass}>
                Email
              </label>
              <input
                id="owner-email"
                type="email"
                autoComplete="off"
                required
                value={ownerEmail}
                onChange={(event) => setOwnerEmail(event.target.value)}
                className={inputClass}
              />
            </div>
          </div>
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-medium">Company info</h2>
          <div className="mt-4 space-y-4">
            <div>
              <label htmlFor="company-name" className={labelClass}>
                Business name
              </label>
              <input
                id="company-name"
                type="text"
                autoComplete="organization"
                required
                maxLength={120}
                value={companyName}
                onChange={(event) => setCompanyName(event.target.value)}
                className={inputClass}
              />
              {slug !== '' && (
                <p className="mt-1 text-sm text-slate-500">
                  Workspace URL: <code className="font-mono text-xs">/w/{slug}</code>
                </p>
              )}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="company-vertical" className={labelClass}>
                  Trade
                </label>
                <select
                  id="company-vertical"
                  value={vertical}
                  onChange={(event) => setVertical(TenantVerticalSchema.parse(event.target.value))}
                  className={inputClass}
                >
                  {TenantVerticalSchema.options.map((option) => (
                    <option key={option} value={option}>
                      {VERTICAL_LABELS[option]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="company-timezone" className={labelClass}>
                  Timezone
                </label>
                <select
                  id="company-timezone"
                  value={timezone}
                  onChange={(event) => setTimezone(event.target.value)}
                  className={inputClass}
                >
                  {US_TIMEZONES.map((tz) => (
                    <option key={tz} value={tz}>
                      {tz.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-medium">Account info</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <div>
              <label htmlFor="account-plan" className={labelClass}>
                Plan
              </label>
              <select
                id="account-plan"
                value={plan}
                onChange={(event) => setPlan(TenantPlanSchema.parse(event.target.value))}
                className={inputClass}
              >
                {TenantPlanSchema.options.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="account-status" className={labelClass}>
                Status
              </label>
              <select
                id="account-status"
                value={status}
                onChange={(event) => setStatus(TenantStatusSchema.parse(event.target.value))}
                className={inputClass}
              >
                {TenantStatusSchema.options.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            {plan === 'trial' && (
              <div>
                <label htmlFor="account-trial-days" className={labelClass}>
                  Trial length (days)
                </label>
                <input
                  id="account-trial-days"
                  type="number"
                  min={1}
                  max={90}
                  value={trialDays}
                  onChange={(event) => setTrialDays(Number(event.target.value))}
                  className={inputClass}
                />
              </div>
            )}
          </div>
          <label className="mt-4 flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={createStripeCustomer}
              onChange={(event) => setCreateStripeCustomer(event.target.checked)}
              className="rounded border-slate-300"
            />
            Create a Stripe customer now (requires a live platform Stripe key)
          </label>
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-medium">Business hours</h2>
          <label className="mt-2 flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={seedHours}
              onChange={(event) => setSeedHours(event.target.checked)}
              className="rounded border-slate-300"
            />
            Seed working hours (drives the scheduler&apos;s availability)
          </label>
          {seedHours && (
            <div className="mt-4 space-y-4">
              <div className="flex flex-wrap gap-3">
                {WEEKDAY_LABELS.map((label, weekday) => (
                  <label key={label} className="flex items-center gap-1.5 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={hourDays[weekday] ?? false}
                      onChange={(event) =>
                        setHourDays((days) =>
                          days.map((value, index) =>
                            index === weekday ? event.target.checked : value
                          )
                        )
                      }
                      className="rounded border-slate-300"
                    />
                    {label}
                  </label>
                ))}
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="hours-open" className={labelClass}>
                    Opens
                  </label>
                  <input
                    id="hours-open"
                    type="time"
                    value={openTime}
                    onChange={(event) => setOpenTime(event.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="hours-close" className={labelClass}>
                    Closes
                  </label>
                  <input
                    id="hours-close"
                    type="time"
                    value={closeTime}
                    onChange={(event) => setCloseTime(event.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>
            </div>
          )}
        </section>

        {fieldError !== null && <p className="text-sm text-red-600">{fieldError}</p>}
        {createMutation.isError && <ErrorNotice error={createMutation.error} />}

        <button
          type="submit"
          disabled={createMutation.isPending}
          className="w-full rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {createMutation.isPending ? 'Creating tenant…' : 'Create tenant'}
        </button>
      </form>
    </main>
  );
}
